<template>
  <div class="qir-top-section__wrapper">
    <div class="qir-top-section__info">
      <p class="qir-top-section__brand text-body">
        Finance Magnates
      </p>

      <h1 class="qir-top-section__title main-title">
        Quarterly Intelligence Reports
      </h1>

      <p class="qir-top-section__details text-body">
        Exclusive Data | Trends | Insights
      </p>

      <p class="qir-top-section__description text-body">
        Access premium insights featuring the latest data on trading volumes,
        key trends, and regulatory updates for the FX/CFD brokerage industry.
      </p>

      <p class="qir-top-section__price">
        Price: <b v-if="reportPrice">{{ reportPrice }} EUR</b>
      </p>
    </div>

    <div
      :id="$options.consts.ELEMENT_ID.FORM_SECTION"
      class="qir-top-section__form-wrapper"
    >
      <a-gradient-border class="qir-top-section__form-container">
        <qir-lead-form
          ref="leadForm"
          :is-report-load-in-progress="inProgress"
          :is-report-data-failed-to-load="isReportDataFailedToLoad"
          :report-id="reportId"
          :report-year-quarter="reportYearQuarter"
          class="qir-top-section__lead-form"
        />
      </a-gradient-border>

      <qir-form-section-background-shapes />
    </div>
  </div>
</template>

<script>
import QirLeadForm from 'sections/_qir/QirTopSection/QirLeadForm/index.vue'
import { COOKIE_ENTITY_TYPE } from 'enums/cmp'
import { PROP_TYPES, propValidator } from '@fmpedia/validators'
import AGradientBorder from 'shared/AGradientBorder/index.vue'
import QirFormSectionBackgroundShapes from 'sections/_qir/QirTopSection/QirFormSectionBackgroundShapes/index.vue'
import { ELEMENT_ID } from 'enums/element-id'
import debounce from 'debounce'
import { pick } from 'ramda'

export default {
  name: 'QirTopSection',
  components: {
    QirFormSectionBackgroundShapes,
    AGradientBorder,
    QirLeadForm
  },
  consts: {
    COOKIE_ENTITY_TYPE,
    ELEMENT_ID
  },
  props: {
    reportData: propValidator([PROP_TYPES.OBJECT], false, null),
    inProgress: propValidator([PROP_TYPES.BOOLEAN]),
    isReportDataFailedToLoad: propValidator([PROP_TYPES.BOOLEAN])
  },
  data() {
    return {
      fmAnnualReportUrl: this.$helper.generateFmUrl(
        '/intelligence/products/annual/',
        this
      ),
      fmAnnualReportQuery: null,
      lastFormHeight: null,
      onLeadDataUpdateDebounced: debounce(this.updateFmAnnualReportQuery, 200)
    }
  },
  computed: {
    reportPrice() {
      return this.reportData?.Price
    },
    reportId() {
      return this.reportData?.Id
    },
    reportYearQuarter() {
      return this.reportData?.YearQuarter
    }
  },
  methods: {
    /**
     * Remap lead data to FM form data format
     */
    getRemappedLeadDataToQueryParamsObj(leadData) {
      const remapped = this.$helper.remapObjectKeys(leadData, {
        firstName: 'FirstName',
        lastName: 'LastName',
        email: 'Email',
        companyName: 'Company',
        vat: 'ValueAddedTaxIdentifier'
      })

      const CountryId = leadData?.country?.Id
      const CountryName = leadData?.country?.Name

      return {
        ...pick(
          [
            'FirstName',
            'LastName',
            'Email',
            'Company',
            'ValueAddedTaxIdentifier'
          ],
          remapped
        ),
        ...(CountryId ? { CountryId } : {}),
        ...(CountryName ? { CountryName } : {})
      }
    },
    /**
     * Important: the format of the resulting query parameters MUST
     * be supported by FM's form prefill functionality
     */
    generateQueryForLeadData(leadData) {
      const remappedLeadData = this.getRemappedLeadDataToQueryParamsObj(
        leadData
      )
      return this.$helper.objectToQueryString(remappedLeadData)
    },
    updateFmAnnualReportQuery(leadData) {
      this.fmAnnualReportQuery = this.generateQueryForLeadData(leadData)
    }
  }
}
</script>

<style lang="scss" scoped>
.qir-top-section__wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;

  $margin-bottom: (
    $above-desktop-xl: 64px,
    $mobile: 32px
  );

  @include setStyleByBreakpoint('margin-bottom', $margin-bottom);

  @include desktop-md {
    flex-direction: column;
    align-items: flex-start;
  }

  .qir-top-section__info {
    z-index: 2;

    $width: (
      $above-desktop-xl: 636px,
      $desktop-md: auto
    );

    $margin-right: (
      $above-desktop-xl: 100px,
      $desktop-md: 30px,
      $tablet: 0
    );

    @include setStyleByBreakpoint('margin-right', $margin-right);
    @include setStyleByBreakpoint('width', $width);
  }

  .qir-top-section__brand {
    color: $c--text-secondary;

    $margin-bottom: (
      $above-desktop-xl: 16px,
      $desktop-md: 8px
    );

    $font-size: (
      $desktop-md: 16px
    );

    $line-height: (
      $desktop-md: 24px
    );

    @include setStyleByBreakpoint('line-height', $line-height);
    @include setStyleByBreakpoint('font-size', $font-size);
    @include setStyleByBreakpoint('margin-bottom', $margin-bottom);
  }

  .qir-top-section__title {
    $margin-bottom: (
      $above-desktop-xl: 16px,
      $desktop-md: 8px
    );

    $max-width: (
      $above-desktop-xl: 400px,
      $desktop-md: 100%
    );

    @include setStyleByBreakpoint('max-width', $max-width);
    @include setStyleByBreakpoint('margin-bottom', $margin-bottom);
  }

  .qir-top-section__details {
    color: $c--text-secondary;

    $margin-bottom: (
      $above-desktop-xl: 36px,
      $mobile: 24px
    );

    $font-size: (
      $desktop-md: 16px
    );

    $line-height: (
      $desktop-md: 24px
    );

    @include setStyleByBreakpoint('line-height', $line-height);
    @include setStyleByBreakpoint('font-size', $font-size);
    @include setStyleByBreakpoint('margin-bottom', $margin-bottom);
  }

  .qir-top-section__description {
    $margin-bottom: (
      $above-desktop-xl: 12px
    );

    @include setStyleByBreakpoint('margin-bottom', $margin-bottom);
  }

  .qir-top-section__price {
    $font-size: (
      $above-desktop-xl: 36px,
      $desktop-md: 30px,
      $mobile: 24px
    );

    $line-height: (
      $above-desktop-xl: 44px,
      $desktop-md: 38px,
      $mobile: 32px
    );

    $margin-bottom: (
      $above-desktop-xl: 12px
    );

    $font-weight: (
      $tablet: 600
    );

    @include setStyleByBreakpoint('font-weight', $font-weight);
    @include setStyleByBreakpoint('margin-bottom', $margin-bottom);
    @include setStyleByBreakpoint('line-height', $line-height);
    @include setStyleByBreakpoint('font-size', $font-size);
  }

  .qir-top-section__form-wrapper {
    position: relative;
    flex-shrink: 0;

    $width: (
      $above-desktop-xl: 576px,
      $mobile: 100%
    );

    $margin: (
      $desktop-md: 0 auto
    );

    @include setStyleByBreakpoint('margin', $margin);
    @include setStyleByBreakpoint('width', $width);

    .qir-top-section__form-container {
      border-radius: 16px;
      position: relative;
      z-index: 2;
    }
  }

  .qir-top-section__purchase-text {
    $margin-bottom: (
      $desktop-md: 54px,
      $mobile: 24px
    );

    @include setStyleByBreakpoint('margin-bottom', $margin-bottom);
  }
}
</style>
